// Klinik Bilgileri — kartlı detay sekmesi (design-principles §8.1, referans:
// Hasta Detay › Kişisel Bilgiler): slate50 zemin, ikonlu SectionCard'lar,
// InfoRow satırları (variant="standard"). Her kart kendi kaydet butonuyla
// bağımsız kaydeder; yetkisiz alan salt okunur (alt çizgisiz) görünür.
// Yükleme: kart çerçeveleri ve başlıklar hemen çizilir, iskelet yalnız değerlerde.

import React, { useState, useEffect } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import {
  BusinessOutlined as BusinessIcon,
  PlaceOutlined as PlaceIcon,
} from "@mui/icons-material";
import { useAlert } from "../../../context/AlertProvider";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { ActionButton, FormSelect, FormTextField, InfoRow } from "../../../components/atoms";
import { SectionCard } from "../../../components/molecules";
import ClinicWhatsAppCard from "./ClinicWhatsAppCard";
import { COLORS, CARD_GAP, CARD_PAGE_PAD, dashboardSectionSx } from "../../../styles/uiConstants";
import {
  useGetClinicInfoMutation,
  useUpdateClinicNameMutation,
  useUpdateClinicInfoMutation,
  useUpdateClinicContactMutation,
  useUpdateClinicTaxMutation,
} from "../../../store/clinicApi";

const CORPORATE_OPTIONS = [
  { value: "false", label: "Bireysel" },
  { value: "true", label: "Kurumsal" },
];

const cardSx = { ...dashboardSectionSx, display: "flex", flexDirection: "column" };

// InfoRow + yüklenirken değer iskeleti. Bileşen dışında: içeride tanımlansa her
// render'da yeni tip olur, input'lar remount olup odak kaybolur.
const Row = ({ label, noBorder, loading, children }) => (
  <InfoRow label={label} noBorder={noBorder}>
    {loading ? <Skeleton variant="text" width="55%" height={26} /> : children}
  </InfoRow>
);

export default function ClinicInfoTab({ clinicId, token, palette, onSave }) {
  const { showSuccess, showError } = useAlert();

  const canName = useHasPermission("admin-clinics:update-name");
  const canEmail = useHasPermission("admin-clinics:update-email");
  const canContact = useHasPermission("admin-clinics:update-contact");
  const canTax = useHasPermission("admin-clinics:update-tax");

  const [getClinicInfo, { data: infoData }] = useGetClinicInfoMutation();
  const [updateName, { isLoading: savingName }] = useUpdateClinicNameMutation();
  const [updateInfo, { isLoading: savingInfo }] = useUpdateClinicInfoMutation();
  const [saveContact, { isLoading: savingContact }] = useUpdateClinicContactMutation();
  const [saveTax, { isLoading: savingTax }] = useUpdateClinicTaxMutation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const [isCorporate, setIsCorporate] = useState(false);
  const [taxNumber, setTaxNumber] = useState("");
  const [taxOffice, setTaxOffice] = useState("");

  useEffect(() => {
    if (token && clinicId) getClinicInfo({ token, clinicId });
  }, [token, clinicId, getClinicInfo]);

  const clinic = infoData?.data?.clinic;
  // Kayıt sonrası yeniden çekmede alanlar iskelete dönmesin: yalnız ilk yükleme
  const showSkeleton = !clinic;

  useEffect(() => {
    if (!clinic) return;
    setName(clinic.name || "");
    setEmail(clinic.email || "");
    setPhone(clinic.phone || "");
    setWebsite(clinic.website || "");
    setDescription(clinic.description || "");
    setCity(clinic.address?.city || "");
    setDistrict(clinic.address?.district || "");
    setFullAddress(clinic.address?.fullAddress || "");
    setIsCorporate(clinic.isCorporate || false);
    setTaxNumber(clinic.taxNumber || "");
    setTaxOffice(clinic.taxOffice || "");
  }, [clinic]);

  const ok = (res) => res?.success || res?.status === "success";
  const errMsg = (e) => e?.data?.message || "Bir hata oluştu";

  const handleSaveBasicInfo = async () => {
    try {
      const updates = [];
      if (canName && name !== (clinic?.name || "")) {
        updates.push(updateName({ token, clinicId, name }).unwrap());
      }
      if (canEmail && email !== (clinic?.email || "")) {
        updates.push(updateInfo({ token, clinicId, email }).unwrap());
      }
      const taxChanged =
        isCorporate !== Boolean(clinic?.isCorporate) ||
        taxNumber !== (clinic?.taxNumber || "") ||
        taxOffice !== (clinic?.taxOffice || "");
      if (canTax && taxChanged) {
        updates.push(saveTax({ token, clinicId, isCorporate, taxNumber, taxOffice }).unwrap());
      }
      if (updates.length === 0) {
        showSuccess("Değişiklik yok");
        return;
      }
      const results = await Promise.all(updates);
      const allOk = results.every(ok);
      if (allOk) {
        showSuccess("Klinik bilgileri güncellendi");
        getClinicInfo({ token, clinicId });
        onSave?.();
      } else {
        showError("Bazı alanlar güncellenemedi");
      }
    } catch (e) { showError(errMsg(e)); }
  };

  const handleSaveContact = async () => {
    try {
      const r = await saveContact({ token, clinicId, phone, website, description, address: { city, district, fullAddress } }).unwrap();
      if (ok(r)) { showSuccess("İletişim bilgileri güncellendi"); }
      else { showError(r?.message); }
    } catch (e) { showError(errMsg(e)); }
  };

  const field = (value, onChange, editable, rest = {}) => (
    <FormTextField
      palette={palette}
      variant="standard"
      size="small"
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={!editable}
      InputProps={{ disableUnderline: !editable }}
      {...rest}
    />
  );

  return (
    <Box
      sx={{
        ...CARD_PAGE_PAD,
        bgcolor: COLORS.slate50,
        minHeight: "100%",
        boxSizing: "border-box",
        display: "grid",
        gap: CARD_GAP,
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        alignContent: "start",
        alignItems: "stretch",
      }}
    >
      <SectionCard
        title="Kimlik ve Vergi"
        subtitle="Klinik adı, kurumsal e-posta ve fatura bilgileri"
        icon={<BusinessIcon sx={{ fontSize: 18 }} />}
        sx={cardSx}
        action={(canName || canEmail || canTax) && !showSkeleton ? (
          <ActionButton variant="save" compact palette={palette} loading={savingName || savingInfo || savingTax} onClick={handleSaveBasicInfo} />
        ) : null}
      >
        <Row loading={showSkeleton} label="Puser No">
          <Typography sx={{ pt: 0.5, fontSize: "0.875rem", fontWeight: 600, color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>
            {clinic?.puserNo != null ? `#${clinic.puserNo}` : "—"}
          </Typography>
        </Row>
        <Row loading={showSkeleton} label="Klinik adı">
          {field(name, setName, canName)}
        </Row>
        <Row loading={showSkeleton} label="E-posta">
          {field(email, setEmail, canEmail, { type: "email" })}
        </Row>
        <Row loading={showSkeleton} label="Klinik tipi">
          <FormSelect
            palette={palette}
            variant="standard"
            size="small"
            fullWidth
            value={isCorporate ? "true" : "false"}
            onChange={(v) => setIsCorporate(v === "true")}
            options={CORPORATE_OPTIONS}
            placeholder={null}
            disabled={!canTax}
            InputProps={{ disableUnderline: !canTax }}
          />
        </Row>
        {/* Bireyselde vergi alanları görünür ama kapalı, "Girilemez" yazar */}
        <Row loading={showSkeleton} label="Vergi no">
          {isCorporate
            ? field(taxNumber, setTaxNumber, canTax, {
                placeholder: "10 haneli",
                inputProps: { maxLength: 10, inputMode: "numeric", style: { fontVariantNumeric: "tabular-nums" } },
              })
            : field("", () => {}, false, { placeholder: "Girilemez" })}
        </Row>
        <Row loading={showSkeleton} label="Vergi dairesi" noBorder>
          {isCorporate ? field(taxOffice, setTaxOffice, canTax) : field("", () => {}, false, { placeholder: "Girilemez" })}
        </Row>
      </SectionCard>

      <SectionCard
        title="İletişim ve Adres"
        subtitle="Hastaların ve personelin gördüğü iletişim bilgileri"
        icon={<PlaceIcon sx={{ fontSize: 18 }} />}
        sx={cardSx}
        action={canContact && !showSkeleton ? (
          <ActionButton variant="save" compact palette={palette} loading={savingContact} onClick={handleSaveContact} />
        ) : null}
      >
        <Row loading={showSkeleton} label="Telefon">
          {field(phone, setPhone, canContact, { inputProps: { inputMode: "tel", style: { fontVariantNumeric: "tabular-nums" } } })}
        </Row>
        <Row loading={showSkeleton} label="Web sitesi">
          {field(website, setWebsite, canContact)}
        </Row>
        <Row loading={showSkeleton} label="İl">
          {field(city, setCity, canContact)}
        </Row>
        <Row loading={showSkeleton} label="İlçe">
          {field(district, setDistrict, canContact)}
        </Row>
        <Row loading={showSkeleton} label="Adres">
          {field(fullAddress, setFullAddress, canContact, { multiline: true, maxRows: 3 })}
        </Row>
        <Row loading={showSkeleton} label="Açıklama" noBorder>
          {field(description, setDescription, canContact, { multiline: true, maxRows: 4 })}
        </Row>
      </SectionCard>

      <ClinicWhatsAppCard clinicId={clinicId} token={token} palette={palette} sx={cardSx} clinicPhone={clinic?.phone} />
    </Box>
  );
}
